import { DELIMITER } from "../../constants";
import type { EventDefinition, ParamsType } from "../../types";
import { redact } from "./redaction";

// Questionnaire events
export const questionnaireEventDefinitions: EventDefinition[] = [
  {
    name: "questionAnswered",
    type: "unstructured",
    makePayload: params => {
      const { section, context } = params as ParamsType;
      const { question, answer } = redact(params as ParamsType);
      // Flatten the answer to a delimited string
      const flatAnswer = Array.isArray(answer)
        ? answer.join(DELIMITER)
        : answer;

      return {
        schema:
          "iglu:com.simplybusiness/form_question_answered/jsonschema/1-0-4",
        data: {
          site: context.site,
          page_index: 1,
          page_name: "quick_to_quote",
          submitted_from: "quick_to_quote_experiment",
          section_name: section,
          question,
          answer: flatAnswer,
          vertical: context.vertical,
          journey_id: context.journeyId,
        },
      };
    },
  },
  {
    name: "primaryDetailSelected",
    type: "unstructured",
    makePayload: params => {
      const {
        context,
        answer,
        vertical,
        searchId,
        selectedListPosition,
        selectionMethod,
      } = params as ParamsType;
      const { site } = context;
      const isUSPage = site === "simplybusiness_us";
      let verticalName = vertical || context.vertical;

      if (verticalName.toLowerCase().indexOf("landlord") > -1) {
        verticalName =
          answer === "residential" ? "Landlord" : "Commercial landlord";
      }

      const data: Record<string, unknown> = {
        site,
        vertical: verticalName,
        primary_detail: answer,
        selected_type: "trade_selector",
        location: window?.location?.pathname ?? "",
      };

      // Always include search_id if present
      if (searchId) {
        data.search_id = searchId;
      }
      // Always include selection_method if present
      if (selectionMethod) {
        data.selection_method = selectionMethod;
      }

      // US only fields
      if (isUSPage) {
        data.selected_list_position =
          selectedListPosition !== undefined
            ? (selectedListPosition + 1).toString()
            : null;
        data.selected_location = "trade_selector_vertical";
        data.business_unit = site;
      }

      return {
        schema:
          "iglu:com.simplybusiness/primary_detail_selected/jsonschema/1-4-0",
        data,
      };
    },
    contexts: ["distributionChannelContext", "serviceChannelContext"],
  },
  {
    name: "helpTextOpened",
    type: "unstructured",
    makePayload: params => {
      const { primaryText, label, context, helpText } = params as ParamsType;

      return {
        schema: "iglu:com.simplybusiness/help_text_opened/jsonschema/1-1-0",
        data: {
          vertical: context.vertical,
          site: context.site,
          primary_text: primaryText,
          help_text: helpText,
          label,
          page_name: "quick_to_quote",
        },
      };
    },
  },
  {
    name: "assumptionsButtonClicked",
    type: "structured",
    makePayload: params => {
      const { source, presentationGroup } = params as ParamsType;

      return {
        action: source,
        category: presentationGroup,
      };
    },
  },
  {
    name: "navButtonClicked",
    type: "structured",
    makePayload: params => {
      const { label, category } = params as ParamsType;

      return {
        label,
        action: "link_click",
        category,
      };
    },
  },
  {
    name: "manualAddressClicked",
    type: "structured",
    makePayload: params => {
      const { category } = params as ParamsType;

      return {
        action: "link_click",
        category,
        label: "Enter address manually",
      };
    },
  },
  {
    name: "assumptionLinkClicked",
    type: "structured",
    makePayload: params => {
      const { label, category, property } = params as ParamsType;

      return {
        action: "assumption_link_click",
        category,
        label,
        property,
      };
    },
  },
  {
    name: "addressLookupFocused",
    type: "structured",
    makePayload: params => {
      const { label, category } = params as ParamsType;

      return {
        action: "address_lookup_focused",
        category,
        label,
      };
    },
  },
  {
    name: "addressLookupTypedIn",
    type: "structured",
    makePayload: params => {
      const { label, category } = params as ParamsType;

      return {
        action: "address_lookup_typed_in",
        category,
        label,
      };
    },
  },
  {
    name: "addressOptionsDisplayed",
    type: "structured",
    makePayload: params => {
      const { label, category } = params as ParamsType;

      return {
        action: "address_options_displayed",
        category,
        label,
      };
    },
  },
  {
    name: "tradeChanged",
    type: "structured",
    makePayload: params => {
      const { label, property } = params as ParamsType;

      return {
        action: "change_your_selected_profession_button_clicked",
        category: "change_your_selected_profession_button",
        label: `Question: ${label}`,
        property, // Answer before change
      };
    },
  },
  {
    name: "stateQuestionAnswered",
    type: "unstructured",
    makePayload: params => {
      const { context, answer, selectedCoverages } = params as ParamsType & {
        selectedCoverages?: string[];
        selectedType?: string;
      };

      const data: Record<string, string> = {
        vertical: context.vertical,
        selected_type: context.journeyName ?? "usa",
        location: window?.location?.pathname ?? "",
        url: window?.location?.href ?? "",
        state: answer,
        selection_method: "dropdown",
      };

      if (selectedCoverages?.length) {
        data.products = selectedCoverages.join(",");
      }

      return {
        schema:
          "iglu:com.simplybusiness/state_question_answered/jsonschema/1-0-0",
        data,
      };
    },
  },
];
